import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  NgZone,
  ViewChild,
  inject,
} from '@angular/core';

const TOOLTIP_EDGE_MARGIN = 8;
const TOOLTIP_GAP = 8;
let nextTooltipId = 0;

@Component({
  selector: 'sm-overflow-tooltip',
  templateUrl: './sm-overflow-tooltip.component.html',
  styleUrls: ['./sm-overflow-tooltip.component.scss'],
  standalone: false,
})
export class SmOverflowTooltipComponent implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private initialMeasureTimer: number | null = null;
  private positionTimer: number | null = null;
  private panelAttachedToBody = false;
  private resizeObserver: ResizeObserver | null = null;

  @Input() text = '';
  @Input() focusable = true;
  @Input() always = false;

  @ViewChild('trigger', {read: ElementRef}) private readonly triggerRef?: ElementRef<HTMLElement>;
  @ViewChild('tooltipPanel', {read: ElementRef}) private readonly tooltipPanelRef?: ElementRef<HTMLElement>;

  isOverflowing = false;
  tooltipVisible = false;
  tooltipReady = false;
  tooltipLeft = 0;
  tooltipTop = 0;
  readonly tooltipId = `sm-overflow-tooltip-${++nextTooltipId}`;

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.text.trim()) {
      return;
    }

    this.initialMeasureTimer = window.setTimeout(() => {
      this.initialMeasureTimer = null;
      this.updateOverflowState();
      this.changeDetectorRef.detectChanges();
    }, 0);

    if (typeof ResizeObserver !== 'undefined' && this.triggerRef) {
      this.resizeObserver = new ResizeObserver(() => {
        this.ngZone.run(() => {
          this.updateOverflowState();
          this.changeDetectorRef.markForCheck();
        });
      });
      this.resizeObserver.observe(this.triggerRef.nativeElement);
    }
  }

  handleMouseEnter(): void {
    this.showIfOverflowing();
  }

  handleMouseLeave(): void {
    this.hideTooltip();
  }

  handleFocus(): void {
    this.showIfOverflowing();
  }

  handleBlur(): void {
    this.hideTooltip();
  }

  handleTriggerClick(): void {
    this.showIfOverflowing();
  }

  showIfOverflowing(): void {
    if (!this.text.trim()) {
      return;
    }

    this.updateOverflowState();
    if (!this.isOverflowing) {
      this.hideTooltip();
      return;
    }

    this.tooltipVisible = true;
    this.tooltipReady = false;
    this.schedulePosition();
  }

  hideTooltip(): void {
    this.cancelPositionTimer();
    this.detachPanelFromBody();
    this.tooltipVisible = false;
    this.tooltipReady = false;
  }

  @HostListener('document:pointerdown', ['$event'])
  handleDocumentPointerDown(event: Event): void {
    if (!this.isInsideTooltip(event.target)) {
      this.hideTooltip();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.triggerRef?.nativeElement.blur();
    this.hideTooltip();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  handleViewportChange(): void {
    this.hideTooltip();
    this.updateOverflowState();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.cancelInitialMeasureTimer();
    this.hideTooltip();
  }

  private updateOverflowState(): void {
    const trigger = this.triggerRef?.nativeElement;
    this.isOverflowing = this.always || Boolean(trigger && trigger.scrollWidth > trigger.clientWidth + 1);

    if (!this.isOverflowing && this.tooltipVisible) {
      this.hideTooltip();
    }
  }

  private schedulePosition(): void {
    this.cancelPositionTimer();
    this.positionTimer = window.setTimeout(() => {
      this.positionTimer = null;
      this.positionTooltip();
    }, 0);
  }

  private positionTooltip(): void {
    const trigger = this.triggerRef?.nativeElement;
    const panel = this.tooltipPanelRef?.nativeElement;
    if (!trigger || !panel || !this.tooltipVisible) {
      return;
    }

    this.attachPanelToBody(panel);

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const maxLeft = Math.max(TOOLTIP_EDGE_MARGIN, window.innerWidth - panelRect.width - TOOLTIP_EDGE_MARGIN);
    const centeredLeft = triggerRect.left + (triggerRect.width - panelRect.width) / 2;
    const left = Math.min(Math.max(centeredLeft, TOOLTIP_EDGE_MARGIN), maxLeft);
    const belowTop = triggerRect.bottom + TOOLTIP_GAP;
    const aboveTop = triggerRect.top - panelRect.height - TOOLTIP_GAP;
    const top = belowTop + panelRect.height <= window.innerHeight - TOOLTIP_EDGE_MARGIN
      ? belowTop
      : Math.max(TOOLTIP_EDGE_MARGIN, aboveTop);

    this.tooltipLeft = left;
    this.tooltipTop = top;
    this.tooltipReady = true;
  }

  private attachPanelToBody(panel: HTMLElement): void {
    if (this.panelAttachedToBody || panel.parentElement === this.document.body) {
      this.panelAttachedToBody = true;
      return;
    }

    this.renderer.appendChild(this.document.body, panel);
    this.panelAttachedToBody = true;
  }

  private detachPanelFromBody(): void {
    const panel = this.tooltipPanelRef?.nativeElement;
    if (!this.panelAttachedToBody || !panel || panel.parentElement !== this.document.body) {
      this.panelAttachedToBody = false;
      return;
    }

    this.renderer.removeChild(this.document.body, panel);
    this.panelAttachedToBody = false;
  }

  private cancelPositionTimer(): void {
    if (this.positionTimer === null) {
      return;
    }

    window.clearTimeout(this.positionTimer);
    this.positionTimer = null;
  }

  private cancelInitialMeasureTimer(): void {
    if (this.initialMeasureTimer === null) {
      return;
    }

    window.clearTimeout(this.initialMeasureTimer);
    this.initialMeasureTimer = null;
  }

  private isInsideTooltip(target: EventTarget | null): boolean {
    if (!(target instanceof Node)) {
      return false;
    }

    return this.elementRef.nativeElement.contains(target)
      || (this.tooltipPanelRef?.nativeElement.contains(target) ?? false);
  }
}
